import { Form } from "@circle-react-uikit/Form";
import { Loader } from "@circle-react-uikit/Loader";
import { Typography } from "@circle-react-uikit/Typography";
import { useDuplicatePost } from "@circle-react/components/Spaces/ImageSpace/DuplicatePostModal/hooks/useDuplicatePost";
import type { DuplicateParams } from "@circle-react/components/Spaces/ImageSpace/DuplicatePostModal/types";
import { useSpacesContext } from "@circle-react/contexts/spacesContext";
import { isPublishedPost } from "@circle-react/helpers/postHelpers";
import { Modal } from "@circleco/compass/components/Modal";
import { isFunction } from "lodash";

import { t } from "@/i18n-js/instance";
import { SpaceSelectCompass } from "@/react/components/shared/SpaceSelectCompass";

import type { DuplicateImagePostModalProps } from "./types";

const FORM_ID = "duplicate-image-post-modal-form";

interface DuplicateImagePostModalComponentProps extends DuplicateImagePostModalProps {
  onOpenChange: (open: boolean) => void;
  open: boolean;
}

export const DuplicateImagePostModalComponent = ({
  open,
  onOpenChange,
  post,
  onSuccess,
}: DuplicateImagePostModalComponentProps) => {
  const { isLoading: isLoadingSpaces, helpers } = useSpacesContext();

  const { duplicate, isDuplicating } = useDuplicatePost({
    post,
    onSuccess: duplicatedPost => {
      isFunction(onSuccess) && onSuccess(duplicatedPost);
      onOpenChange(false);
    },
  });

  const spaces = helpers.imageSpacesWithPostCreationAllowed();

  return (
    <Modal.Root open={open} onOpenChange={onOpenChange} size="md">
      <Modal.Content>
        <Modal.Header title={t("duplicate_post")} />
        <Modal.Body>
          <Form
            id={FORM_ID}
            defaultValues={{
              space_id: post.space_id,
              send_notifications: false,
            }}
            onSubmit={(data: DuplicateParams) => {
              duplicate(data);
            }}
          >
            <Form.Item
              name="space_id"
              label={t("space")}
              description={t("duplicate_post_to_space")}
              rules={{
                required: t("events.create.form.space_required_error"),
              }}
              isFirstChild
              hideBorder
              fullWidth
            >
              {isLoadingSpaces ? (
                <div className="flex min-h-11 items-center py-2">
                  <Loader />
                </div>
              ) : (
                <SpaceSelectCompass name="space_id" spaces={spaces} />
              )}
            </Form.Item>
            {isPublishedPost(post) && (
              <>
                <Typography.LabelXs color="text-light">
                  {t("comments_wont_be_duplicated")}
                </Typography.LabelXs>
                <Form.Item
                  name="send_notifications"
                  label={t("duplicate_post_send_notifications")}
                  hideDescription
                  inlineReverse
                  hideBorder
                  fullWidth
                >
                  <Form.ToggleSwitch
                    name="send_notifications"
                    variant="small"
                  />
                </Form.Item>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer
          secondaryAction={{
            label: t("cancel"),
            onClick: () => onOpenChange(false),
          }}
          primaryAction={{
            label: isDuplicating ? t("duplicating") : t("duplicate"),
            type: "submit",
            form: FORM_ID,
            disabled: isLoadingSpaces,
            loading: isDuplicating,
          }}
        />
      </Modal.Content>
    </Modal.Root>
  );
};
